/**
 * FlzFamilyTree — Agent family hierarchy visualization
 * GET /api/flzhub/family-tree → { nodes: [{id, agent_name, level}], edges: [{mentor_id, mentee_id}] }
 */
import { Users } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';

interface FamilyNode { id: number; agent_name: string; level: number }
interface FamilyEdge { mentor_id: number; mentee_id: number }

const LEVEL_COLORS: Record<number, string> = {
  1: '#ec4899', // Anne/Baba
  2: '#6366f1', // Experts
  3: '#10b981', // Level 3
  4: '#f59e0b', // Level 4
  5: '#3b82f6', // Level 5
}

export const FlzFamilyTree: React.FC = () => {
  const [nodes, setNodes] = useState<FamilyNode[]>([])
  const [edges, setEdges] = useState<FamilyEdge[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<FamilyNode | null>(null)
  const [familyOf, setFamilyOf] = useState<any>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    apiFetch(`${API_URL}/api/flzhub/family-tree`)
      .then(r => r.json())
      .then(d => { setNodes(d.nodes ?? []); setEdges(d.edges ?? []) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const loadFamily = async (agentId: number) => {
    try {
      const r = await apiFetch(`${API_URL}/api/flzhub/family/${agentId}`)
      const d = await r.json()
      setFamilyOf(d)
    } catch { /* */ }
  }

  const handleNodeClick = (node: FamilyNode) => {
    setSelected(node)
    void loadFamily(node.id)
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: 'var(--os-muted)', fontSize: 12 }}>
      Loading family tree...
    </div>
  )

  // Layout: group by level, position nodes
  const byLevel: Record<number, FamilyNode[]> = {}
  nodes.forEach(n => { if (!byLevel[n.level]) byLevel[n.level] = []; byLevel[n.level].push(n) })
  const levels = Object.keys(byLevel).map(Number).sort()

  const W = 800, H = 500
  const nodePositions: Record<number, { x: number; y: number }> = {}
  levels.forEach((lvl, li) => {
    const levelNodes = byLevel[lvl]
    const y = 40 + li * (H / (levels.length + 1))
    levelNodes.forEach((n, ni) => {
      const x = (W / (levelNodes.length + 1)) * (ni + 1)
      nodePositions[n.id] = { x, y }
    })
  })

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--os-border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <Users size={14} color="#ec4899" />
        <span style={{ fontSize: 12, fontWeight: 700 }}>Family Tree</span>
        <span style={{ fontSize: 10, color: 'var(--os-muted)', marginLeft: 4 }}>{nodes.length} agents · {edges.length} connections</span>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* SVG Tree */}
        <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minHeight: 400, background: 'rgba(0,0,0,0.2)', borderRadius: 12 }}>
            {/* Edges */}
            {edges.map((e, i) => {
              const from = nodePositions[e.mentor_id]
              const to = nodePositions[e.mentee_id]
              if (!from || !to) return null
              return (
                <line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                  stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
              )
            })}
            {/* Nodes */}
            {nodes.map(n => {
              const pos = nodePositions[n.id]
              if (!pos) return null
              const color = LEVEL_COLORS[n.level] || '#6b7280'
              const isSelected = selected?.id === n.id
              return (
                <g key={n.id} onClick={() => handleNodeClick(n)} style={{ cursor: 'pointer' }}>
                  <circle cx={pos.x} cy={pos.y} r={isSelected ? 14 : 10}
                    fill={color} fillOpacity={isSelected ? 1 : 0.7}
                    stroke={isSelected ? '#fff' : color} strokeWidth={isSelected ? 2 : 1} />
                  <text x={pos.x} y={pos.y + 22} textAnchor="middle"
                    fontSize={8} fill="rgba(255,255,255,0.7)" style={{ pointerEvents: 'none' }}>
                    {n.agent_name.split(' ')[0]}
                  </text>
                </g>
              )
            })}
          </svg>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 12, padding: '8px 4px', flexWrap: 'wrap' }}>
            {Object.entries(LEVEL_COLORS).map(([lvl, color]) => (
              <div key={lvl} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--os-muted)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                Level {lvl}
              </div>
            ))}
          </div>
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{ width: 220, borderLeft: '1px solid var(--os-border)', padding: 14, overflowY: 'auto', flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{selected.agent_name}</div>
            <div style={{ fontSize: 10, color: 'var(--os-muted)', marginBottom: 12 }}>Level {selected.level}</div>

            {familyOf && (
              <>
                {familyOf.mentor && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: 'var(--os-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Mentor</div>
                    <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>{familyOf.mentor.agent_name}</div>
                  </div>
                )}
                {familyOf.mentees && familyOf.mentees.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: 'var(--os-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Mentees ({familyOf.mentees.length})</div>
                    {familyOf.mentees.slice(0, 8).map((m: any) => (
                      <div key={m.id} style={{ fontSize: 10, color: 'var(--os-text)', padding: '2px 0' }}>{m.agent_name}</div>
                    ))}
                  </div>
                )}
                {familyOf.siblings && familyOf.siblings.length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--os-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Siblings ({familyOf.siblings.length})</div>
                    {familyOf.siblings.slice(0, 5).map((s: any) => (
                      <div key={s.id} style={{ fontSize: 10, color: 'var(--os-text)', padding: '2px 0' }}>{s.agent_name}</div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
